"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function Obfuscate({
  children
}: {
  children: string;
}) {
  const [decoded, setDecoded] = React.useState("");
  React.useEffect(() => {
    try {
      setDecoded(window.atob(children));
    } catch (e) {
      setDecoded(children);
    }
  }, [children]);
  return <span>{decoded || "..."}</span>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="126826" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
          <Mail className="size-8" />
        </div>
        <h3 className="text-2xl font-bold tracking-tight text-foreground">Zapytanie wysłane!</h3>
        <p className="text-muted-foreground max-w-sm font-medium">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/20 hover:bg-primary/5 text-primary">
          Wyślij kolejne zapytanie
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Imię i nazwisko jest wymagane"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Imię i nazwisko <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Jan Kowalski" {...field} className="bg-background/50 border-input focus:border-primary focus:ring-primary/20 rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Adres / Miejscowość</FormLabel>
              <FormControl>
                <Input placeholder="Pszczyna, ul. Słoneczna 5" {...field} className="bg-background/50 border-input focus:border-primary focus:ring-primary/20 rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Adres e-mail jest wymagany",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Nieprawidłowy adres e-mail"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Adres e-mail <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="jan@poczta.pl" {...field} className="bg-background/50 border-input focus:border-primary focus:ring-primary/20 rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Numer telefonu</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+48 123 456 789" {...field} className="bg-background/50 border-input focus:border-primary focus:ring-primary/20 rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="preferred_contact_time" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Preferowany czas kontaktu</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-background/50 border-input focus:border-primary focus:ring-primary/20 rounded-xl">
                  <SelectValue placeholder="Wybierz porę dnia" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Rano (8:00‑12:00)">Rano (8:00‑12:00)</SelectItem>
                <SelectItem value="Popołudnie (12:00‑16:00)">Popołudnie (12:00‑16:00)</SelectItem>
                <SelectItem value="Wieczór (16:00‑20:00)">Wieczór (16:00‑20:00)</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-xs font-mono uppercase tracking-wider opacity-70">Zadzwonimy w wybranym przedziale</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_details" rules={{
      required: "Opis instalacji jest wymagany"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Szczegóły instalacji <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Np. płukanie 10 grzejników, czyszczenie podłogówki, wiek instalacji..." className="min-h-[120px] resize-y bg-background/50 border-input focus:border-primary focus:ring-primary/20 rounded-xl" {...field} />
            </FormControl>
            <FormDescription className="text-xs font-mono uppercase tracking-wider opacity-70">Wstępny opis pozwoli nam przygotować darmową wycenę</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-medium">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full py-7 text-lg font-bold tracking-tight rounded-xl bg-gradient-to-r from-primary to-chart-2 text-primary-foreground shadow-xl shadow-primary/20 transition-all hover:shadow-primary/40 hover:-translate-y-1">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Wysyłanie danych...
          </> : "Poproś o darmową wycenę"}
      </Button>
    </Form>;
}
export default function LetsTalkAboutProject() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const contactInfo = [{
    icon: <MapPin className="size-5 text-primary" />,
    title: "Lokalizacja",
    details: "UHN6Y3p5bmE="
  }, {
    icon: <Phone className="size-5 text-primary" />,
    title: "Zadzwoń do nas",
    details: "KzQ4IDc5NiAyODAgMzE1"
  }, {
    icon: <Mail className="size-5 text-primary" />,
    title: "Napisz e-mail",
    details: "kontakt@hydroflush.pl"
  }];
  return <section id="zapytaj-o-darmową-wycenę" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden relative" ref={sectionRef}>
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-chart-2/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          
          <div className={`space-y-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-foreground mb-6">
                Czysta instalacja – niższe rachunki
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed font-medium">Zadbaj o wydajność swojego ogrzewania już dziś. Jesteśmy lokalnymi specjalistami, gotowymi przywrócić sprawność Twojej instalacji CO.</p>
            </div>

            
            <div className="relative w-full h-64 sm:h-80 rounded-3xl overflow-hidden border border-border/50 shadow-lg group">
              <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606250/img/service_area_map_overlay.png)] bg-cover bg-center grayscale opacity-60 transition-transform duration-1000 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent !bg-[url(https://hydroflush.pl/wp-content/uploads/2026/03/map_grayscale_bg.webp)]" />
              
              
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center animate-bounce">
                <div className="bg-primary text-primary-foreground p-4 rounded-full shadow-2xl shadow-primary/40">
                  <MapPin className="size-7" />
                </div>
                <div className="w-3 h-3 bg-primary/40 rounded-full mt-2 blur-[2px]" />
              </div>
            </div>

            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {contactInfo.map((info, i) => <div key={i} data-index={i} className="flex flex-col items-start p-6 rounded-2xl bg-white border border-border/50 hover:border-primary/30 hover:shadow-md transition-all duration-300">
                  <div className="bg-accent p-3 rounded-xl shadow-sm mb-4 border border-primary/10">
                    {info.icon}
                  </div>
                  <h4 className="text-xs font-mono font-bold uppercase tracking-widest text-primary mb-2">{info.title}</h4>
                  <p className="text-sm font-bold text-foreground leading-tight">
                    <Obfuscate>{info.details}</Obfuscate>
                  </p>
                </div>)}
            </div>
          </div>

          
          <div className={`transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Card className="border-border/50 shadow-2xl shadow-shadow-color/5 bg-card/95 backdrop-blur-2xl relative overflow-hidden rounded-3xl">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary via-chart-2 to-primary" />
              <CardHeader className="pb-6 pt-10 px-8">
                <CardTitle className="text-3xl font-bold tracking-tight">Darmowa wycena</CardTitle>
                <CardDescription className="text-lg font-medium text-muted-foreground mt-2">Zostaw swoje dane, a nasz ekspert oddzwoni z propozycją terminu i kosztorysem.</CardDescription>
              </CardHeader>
              <CardContent className="px-8 pb-10">
                <FormProvider formKey="free_estimate_form_home" formId="free_estimate_form_home" sectionName="LetsTalkAboutProject" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                project_details: "",
                preferred_contact_time: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                project_details: {
                  required: true
                }
              }} submitText="Wyślij Zapytanie" successMessage="Dziękujemy! Otrzymaliśmy Twoje zgłoszenie. Nasz specjalista skontaktuje się z Tobą najszybciej jak to możliwe, aby omówić szczeguły płukania instalacji." errorMessage="Wystąpił nieoczekiwany błąd. Prosimy o kontakt telefoniczny pod numerem podanym obok.">
                  <ContactFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}